'use client';
import React, { useState, useEffect } from 'react';
import { Clock, MoreHorizontal, Loader2 } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { bookingsService, type Booking } from '@/lib/supabase/bookingDataService';

export default function AdminTodayTimeline() {
  const [appointments, setAppointments] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [todayLabel, setTodayLabel] = useState('');

  useEffect(() => {
    const now = new Date();
    setTodayLabel(now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }));

    bookingsService.getTodayBookings()
      .then(setAppointments)
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden h-full">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-foreground">Today&apos;s Schedule</p>
          <p className="text-xs text-muted-foreground">{todayLabel}</p>
        </div>
        {!isLoading && (
          <span className="text-xs font-semibold text-primary tabular-nums">{appointments.length} appts</span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-sm">Loading...</span>
        </div>
      ) : appointments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground/50">
          <Clock size={24} className="mb-2" />
          <p className="text-sm">No appointments today</p>
        </div>
      ) : (
        <div className="divide-y divide-border overflow-y-auto scrollbar-thin" style={{ maxHeight: '360px' }}>
          {appointments.map((appt) => (
            <div key={appt.id} className="px-4 py-3 hover:bg-muted/20 transition-colors group">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="flex items-center gap-1 text-xs font-mono text-muted-foreground">
                      <Clock size={11} />
                      {appt.time || '—'}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{appt.client}</p>
                    <p className="text-xs text-muted-foreground truncate">{appt.service}</p>
                    <p className="text-xs text-muted-foreground/60 truncate">{appt.vehicle}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 flex-shrink-0">
                  <StatusBadge type="booking" status={appt.status} size="sm" />
                  <button className="p-1 rounded text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-all">
                    <MoreHorizontal size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}